import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { triggerCreditsGate } from "@/lib/credits-gate";
import type { AdAngleSet, Product } from "@shared/schema";

interface CreateSetModalProps {
  brandId: number;
  products: Product[];
  onClose: () => void;
  onCreated: (set: AdAngleSet) => void;
  /** Pre-select + lock the product (failed-set retry flow). */
  initialProductId?: number;
  /** Prefill the direction textarea (failed-set retry flow). */
  initialDirection?: string;
}

// v7.09 — the funnel grid is gone (Dan: "we got rid of that, it's just
// supposed to be Angles"). The generator writes ONE flat batch, so the modal
// reports the three real phases instead of TOF/MOF/BOF/Retargeting rows.
const PHASE_ORDER = ["context", "writing", "saving"] as const;
const PHASE_LABELS: Record<string, string> = {
  context: "Reading the brand + product",
  writing: "Writing the angles",
  saving: "Saving the set",
};
const COUNT_CHOICES = [5, 10, 15, 20] as const;

/**
 * Stream the angle-set creation SSE response, calling onEvent for each parsed
 * event. Hand-rolled parser for the server's `event: <name>` / `data: <json>`
 * framing, blank-line-delimited chunks — same wire format the Build Campaign
 * stream in AdAnglesPanel uses.
 */
async function streamCreateSet(
  brandId: number,
  body: { productId: number; direction?: string; count?: number },
  onEvent: (eventName: string, data: any) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`/api/brands/${brandId}/angle-sets`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    signal,
  });
  if (!res.ok) {
    // Pre-SSE failure — server sends plain JSON, not an event stream.
    let message = `Request failed (HTTP ${res.status})`;
    try {
      const data = await res.json();
      if (data?.code === "CREDITS_EXHAUSTED") {
        triggerCreditsGate(Number(data.balance ?? 0), Number(data.needed ?? 0));
        message = "Not enough credits to generate this set.";
      } else if (data?.error === "product_required") {
        message = "Pick a product first.";
      } else if (data?.error === "product_not_found") {
        message = "That product couldn't be found.";
      } else if (typeof data?.error === "string") {
        message = data.error;
      } else if (typeof data?.message === "string") {
        message = data.message;
      }
    } catch {
      // ignore — fall back to generic message
    }
    throw new Error(message);
  }

  const reader = res.body?.getReader();
  if (!reader) return;
  const decoder = new TextDecoder();
  let buffer = "";
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      let eventName = "message";
      let data = "";
      for (const line of chunk.split("\n")) {
        if (line.startsWith("event:")) eventName = line.slice(6).trim();
        else if (line.startsWith("data:")) data += line.slice(5).trim();
      }
      if (data) {
        try {
          onEvent(eventName, JSON.parse(data));
        } catch {
          onEvent(eventName, data);
        }
      }
    }
  }
}

export function CreateSetModal({
  brandId,
  products,
  onClose,
  onCreated,
  initialProductId,
  initialDirection,
}: CreateSetModalProps) {
  const singleProduct = products.length === 1 ? products[0] : null;
  // Locked when explicitly pre-filled (failed-set retry) or when only one product exists.
  const lockedProduct =
    (initialProductId != null ? products.find((p) => p.id === initialProductId) : undefined) ??
    singleProduct;
  const [productId, setProductId] = useState<string>(lockedProduct ? String(lockedProduct.id) : "");
  const [direction, setDirection] = useState(initialDirection ?? "");
  const [isGenerating, setIsGenerating] = useState(false);
  const [phase, setPhase] = useState<string | null>(null);
  const [count, setCount] = useState<number>(5);
  const [error, setError] = useState<string | null>(null);

  const lockedProductId = lockedProduct?.id;
  useEffect(() => {
    if (lockedProductId != null) setProductId(String(lockedProductId));
  }, [lockedProductId]);

  const noProducts = products.length === 0;
  const canSubmit = !noProducts && productId !== "" && !isGenerating;

  // Aborting the fetch only detaches this client from the SSE stream — the
  // server keeps running generateAngleSet() to completion regardless (Express
  // doesn't cancel in-flight work just because the reader disconnects). That
  // is exactly what makes "close mid-generation" safe: the set finishes
  // server-side and the folder grid's 5s poll (refetchInterval while any set
  // is `generating`) picks it up on its own.
  const abortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  async function handleGenerate() {
    if (!productId) return;
    setError(null);
    setPhase(null);
    setIsGenerating(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      let completedSet: AdAngleSet | null = null;
      let streamError: string | null = null;
      await streamCreateSet(
        brandId,
        {
          productId: Number(productId),
          direction: direction.trim() || undefined,
          count,
        },
        (eventName, data) => {
          // Ignore anything that arrives after we've detached (unmount, or
          // the user closed the modal to continue in background).
          if (controller.signal.aborted) return;
          if (eventName === "set_created") {
            // The generating set row now exists — hand off to the folder
            // card (spinner + 5s poll) instead of trapping the user here
            // for the multi-minute generation. Detach the stream; the
            // server finishes regardless.
            toast({
              description:
                "Copy set is generating — it'll go live in the folder in a few minutes.",
            });
            abortRef.current?.abort();
            onClose();
          } else if (eventName === "progress") {
            const { stage } = data as { stage: string };
            setPhase(stage);
          } else if (eventName === "completed") {
            completedSet = (data as { set: AdAngleSet }).set;
          } else if (eventName === "error") {
            streamError = (data as { message?: string })?.message ?? "Generation failed";
          }
        },
        controller.signal,
      );

      if (controller.signal.aborted) return;

      if (streamError) {
        setError(streamError);
        setIsGenerating(false);
        return;
      }
      if (completedSet) {
        onCreated(completedSet);
        return;
      }
      // Stream ended with neither `completed` nor `error` — treat as failure
      // rather than silently closing the modal.
      setError("Generation ended unexpectedly. Please try again.");
      setIsGenerating(false);
    } catch (err) {
      if (controller.signal.aborted) return;
      setError(err instanceof Error ? err.message : String(err));
      setIsGenerating(false);
    }
  }

  function handleCancelOrClose() {
    if (isGenerating) {
      // Detach without waiting — server-side generation keeps running.
      abortRef.current?.abort();
      toast({ description: "Generation continues — the set will appear when ready." });
    }
    onClose();
  }

  return (
    <Dialog open onOpenChange={(o) => !o && handleCancelOrClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Create copy set</DialogTitle>
          <DialogDescription>
            Create concepts for one product. Review the copy and visual direction next, then choose Generate ad images when you are ready. Image generation uses credits.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="create-set-product">Product</Label>
            {noProducts ? (
              <p className="text-sm text-muted-foreground rounded-md border border-dashed px-3 py-2">
                Add a product in the brand Overview, then return here.
              </p>
            ) : lockedProduct ? (
              <div className="text-sm rounded-md border px-3 py-2 bg-muted/30">
                {lockedProduct.name}
              </div>
            ) : (
              <Select value={productId} onValueChange={setProductId} disabled={isGenerating}>
                <SelectTrigger id="create-set-product">
                  <SelectValue placeholder="Choose a product" />
                </SelectTrigger>
                <SelectContent>
                  {products.map((p) => (
                    <SelectItem key={p.id} value={String(p.id)}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {products.find(p => String(p.id) === productId) && (() => {
            const selected = products.find(p => String(p.id) === productId)!;
            return <div className="flex items-start gap-3 rounded-md border p-3" data-testid="selected-concept-product">
              {selected.photos?.[0] && <img src={selected.photos[0]} alt="" className="h-12 w-12 rounded object-contain" />}
              <div className="min-w-0"><p className="text-sm font-medium">Selected product: {selected.name}</p>
                <p className="text-xs text-muted-foreground line-clamp-3">{selected.description || 'Check this product’s details in Overview before continuing.'}</p>
                <p className="text-xs text-muted-foreground">{selected.photos?.length ?? 0} reference photos for this product</p>
              </div>
            </div>;
          })()}

          <div className="space-y-1.5">
            <Label htmlFor="create-set-direction">Direction (optional)</Label>
            <Textarea
              id="create-set-direction"
              value={direction}
              onChange={(e) => setDirection(e.target.value)}
              placeholder="e.g. 30% off summer sale (optional — empty = evergreen)"
              maxLength={300}
              disabled={isGenerating}
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label>How many ad concepts?</Label>
            <div className="flex gap-1.5">
              {COUNT_CHOICES.map((n) => (
                <button
                  key={n}
                  type="button"
                  disabled={isGenerating}
                  onClick={() => setCount(n)}
                  className={`rounded-md border px-3 py-1.5 text-xs font-medium transition-colors ${
                    count === n
                      ? "border-foreground bg-foreground text-background"
                      : "border-input bg-background text-muted-foreground hover:text-foreground"
                  } ${isGenerating ? "opacity-50" : ""}`}
                >
                  {n}
                </button>
              ))}
            </div>
          </div>

          {isGenerating && (
            <div className="space-y-1.5 rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
              {PHASE_ORDER.map((p) => {
                const idx = PHASE_ORDER.indexOf(p);
                const activeIdx = phase ? PHASE_ORDER.indexOf(phase as typeof PHASE_ORDER[number]) : 0;
                const done = idx < activeIdx;
                return (
                  <div key={p} className="flex items-center gap-2 text-xs">
                    {done ? (
                      <span className="text-emerald-600">✓</span>
                    ) : (
                      <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                    )}
                    <span className={done ? "text-foreground" : "text-muted-foreground"}>
                      {PHASE_LABELS[p]}
                      {p === "writing" && !done ? ` — ${count} angle${count === 1 ? "" : "s"}…` : done ? "" : "…"}
                    </span>
                  </div>
                );
              })}
              <div className="pt-0.5 text-[11px] text-muted-foreground">
                This takes a couple of minutes — you can close this and it keeps going.
              </div>
            </div>
          )}

          {error && (
            <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancelOrClose}>
            {isGenerating ? "Continue in background" : "Cancel"}
          </Button>
          <Button onClick={handleGenerate} disabled={!canSubmit}>
            {isGenerating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" />
                Generating…
              </>
            ) : (
              "Create concepts"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
